import { MathUtils } from '@3d-scene/common/util/math-utils';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';

export class NumberUtils {

  public static isNumber(n: any): n is number {
    return typeof n === 'number' && !isNaN(n) && isFinite(n);
  }

  public static equals(a: number, b: number): boolean {
    if (isNaN(a)) {
      return isNaN(b);
    }
    if (isNaN(b)) {
      return false;
    }
    // disable triple-equals check here to treat cases like 'undefined == null' as 'true'
    // eslint-disable-next-line eqeqeq
    return a == b;
  }

  public static numericCompare(a: number, b: number): number {
    if (isNaN(a) && isNaN(b)) {
      return 0;
    }

    if (isNaN(a)) {
      return 1;
    }

    if (isNaN(b)) {
      return -1;
    }

    if (a < b) {
      return -1;
    }

    if (a > b) {
      return 1;
    }

    return 0;
  }

  public static isNotNumber(n: number): boolean {
    return !this.isNumber(n);
  }

  /** Function will return integer value that is closest to 'floatValue' depending on 'roundOffLimit'. */
  public static roundValueByRoundOffLimit(floatValue: number, roundOffLimit: number, minimumValue: number): number {
    const ceilValue: number = Math.ceil(floatValue);
    const floorValue: number = Math.floor(floatValue);
    const calculatedDecidingValue: number = floorValue + roundOffLimit;
    let result: number;
    if (floatValue > calculatedDecidingValue) {
      result = ceilValue;
    } else {
      result = floorValue;
    }
    if (!isNaN(minimumValue) && !isNaN(result)) {
      result = Math.max(minimumValue, result);
    }
    return result;
  }

  public static toInt32(value: number): number {
    if (this.isNotNumber(value)) {
      return NaN;
    }
    // eslint-disable-next-line no-bitwise
    return value >> 0;
  }

  public static integerToHex(intVal: number): string {
    let result: string = parseInt(intVal.toString(), 10).toString(16);
    while (result.length < 2) {
      result = '0' + result;
    }
    return result;
  }

  public static getRoundedValueInMillimeters(value: number): number {
    return this.getRoundedValue(this.getValueInMillimeters(value));
  }

  private static getRoundedValue(value: number): number {
    return MathUtils.roundNumberValue(value, 1);
  }

  private static getValueInMillimeters(value: number): number {
    if (value < 0) {
      return UnitConverter.fromTo('3d', 'mm', value) / -1;
    }
    return UnitConverter.fromTo('3d', 'mm', value);
  }
}
